import type { GetServerSidePropsContext } from "next";
import type { Configuration, EmbedUrl } from "../dataAccess/api/configuration";
import { SSR } from "./environment";

function addCspHashQuotes(attr: string) {
  return attr.startsWith("sha") ? `'${attr}'` : attr;
}

export function getClientNonce() {
  if (SSR) {
    return undefined;
  }
  const nonceMeta = document.querySelector<HTMLMetaElement>(
    'meta[name="resource-nonce"]',
  );
  return nonceMeta?.content;
}

export function addCspHeaders({
  context,
  embedUrls,
  communityToggleSetting,
  hideSupportChat,
  nonce: resourceNonce,
}: {
  context: GetServerSidePropsContext;
  embedUrls: EmbedUrl[];
  communityToggleSetting: Configuration["communityToggleSetting"];
  hideSupportChat: boolean;
  nonce: string;
}) {
  try {
    const frameAncestorsArr: string[] = [];
    let csp = "";
    if (embedUrls?.length) {
      const urls = embedUrls.map(({ url }) => new URL(url).origin);
      if (urls) {
        frameAncestorsArr.push(...urls);
      }
    }

    if (communityToggleSetting?.contentSecurityPolicyEnabled) {
      const nonce = `'nonce-${resourceNonce}'`;
      const cdnImages = process.env.NEXT_PUBLIC_CDN_IMAGES || "";
      const cdn = process.env.NEXT_CDN || "";
      const api = process.env.NEXT_PUBLIC_API_URL || "";
      const {
        fontSrc = [],
        imgSrc = [],
        mediaSrc = [],
        styleSrc: styleSrcConfig = [],
        frameAncestors = [],
        connectSrc = [],
        formAction = [],
      } = communityToggleSetting.contentSecurityPolicy;

      frameAncestorsArr.push(...frameAncestors);

      const enableUnsafeInlineStyle =
        communityToggleSetting.unsafeInlineStyleAllowed;
      const intercomEnabled = !hideSupportChat;

      const styleSrc =
        intercomEnabled || enableUnsafeInlineStyle
          ? styleSrcConfig?.filter((attr) => !attr.startsWith("sha")) // Needed for intercom based on https://community.intercom.com/knowledge-6/is-there-a-way-to-serve-intercom-with-content-security-policy-without-style-src-unsafe-inline-2165
          : styleSrcConfig;

      const defaultSrcCsp = `default-src 'self' ${cdn}`;

      const intercomConnectSrcArr = intercomEnabled
        ? [
            "wss://nexus-websocket-a.intercom.io", // intercom
            "https://api-iam.intercom.io", // intercom
          ]
        : [];

      const intercomFrameSrcArr = intercomEnabled
        ? [
            "https://intercom-sheets.com/", // intercom (when opening help center form it)
          ]
        : [];

      const intercomFormActionArr = intercomEnabled
        ? [
            "https://api-iam.intercom.io", // intercom
          ]
        : [];

      const scriptSrcArr = ["'strict-dynamic'", "'unsafe-eval'", nonce];

      // TODO: uncomment to use a flag, keep process.env.NEXT_ENV === "local"
      // if (process.env.NEXT_ENV === "local") {
      //   scriptSrcArr.push("'unsafe-eval'");
      // }

      const scriptSrcCsp = `script-src ${scriptSrcArr
        .map(addCspHashQuotes)
        .join(" ")}`;

      const styleSrcArr = [
        // REGARDING INLINE STYLES IN ELEMENTS:
        // Notice that style="" attributes in our source code, are still supported,
        // as they are added by Mui dynamically with JS (from our self domain), from Mozilla documentation:
        // "However, styles properties that are set directly on the element's style property will not
        // be blocked, allowing users to safely manipulate styles via JavaScript"
        // https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/Content-Security-Policy/style-src#violation_cases
        "'self'",
        intercomEnabled || enableUnsafeInlineStyle ? "'unsafe-inline'" : nonce, // Needed for intercom based on https://community.intercom.com/knowledge-6/is-there-a-way-to-serve-intercom-with-content-security-policy-without-style-src-unsafe-inline-2165
        "https://fonts.googleapis.com", // google fonts
        ...styleSrc,
      ];
      const styleSrcCsp = `style-src ${styleSrcArr
        .map(addCspHashQuotes)
        .join(" ")}`;

      const imgSrcArr = [
        "'self'",
        "data:", // next.js <Image> component sometimes uses data URLs for optimization, placeholders
        cdnImages,
        "https://i.vimeocdn.com/video/", // embedly
        "https://i.ytimg.com/vi/", // embedly
        ...imgSrc,
      ];
      const imgSrcCsp = `img-src ${imgSrcArr.join(" ")}`;

      const fontSrcArr = [
        // Swiper load icons using data:, no need to add data: here as we are not using those icons
        "'self'",
        cdnImages,
        "https://fonts.gstatic.com", // google fonts
        ...fontSrc,
      ];
      const fontSrcCsp = `font-src ${fontSrcArr.join(" ")}`;

      const mediaSrcArr = ["'self'", ...mediaSrc];
      const mediaSrcCsp = `media-src ${mediaSrcArr.join(" ")}`;

      const connectSrcArr = [
        "'self'",
        cdn,
        cdnImages,
        api,
        "https://api.embedly.com/1/oembed", // embedly
        "https://browser-intake-us3-datadoghq.com", // datadoghq
        "https://www.google.com/recaptcha/", // recaptcha
        ...intercomConnectSrcArr,
        ...connectSrc,
      ];
      const connectSrcCsp = `connect-src ${connectSrcArr.join(" ")}`;

      const objectSrcCsp = "object-src 'self'";
      const baseUriCsp = "base-uri 'self'";

      const frameSrcArr = [
        "'self'",
        "https://td.demdex.net/", // TODO: hardcoded for td
        "https://www.googletagmanager.com/", // TODO: hardcoded for td
        "http://cdn.embedly.com/widgets/", // embedly
        "https://www.google.com/recaptcha/", // recaptcha
        ...intercomFrameSrcArr,
      ];
      const frameSrcCsp = `frame-src ${frameSrcArr.join(" ")}`;

      const formActionArr = ["'self'", ...intercomFormActionArr, ...formAction];
      const formActionCsp = `form-action ${formActionArr.join(" ")}`;

      csp = `${[
        defaultSrcCsp,
        scriptSrcCsp,
        styleSrcCsp,
        imgSrcCsp,
        fontSrcCsp,
        mediaSrcCsp,
        connectSrcCsp,
        objectSrcCsp,
        baseUriCsp,
        frameSrcCsp,
        formActionCsp,
      ]
        .join(";")
        .replace(/[ ]+/g, " ")};`;
    }

    if (frameAncestorsArr?.length) {
      csp += `frame-ancestors ${frameAncestorsArr.join(" ")};`;
    }

    const trimmedCsp = csp.trim();
    if (trimmedCsp) {
      context.res.setHeader("Content-Security-Policy", trimmedCsp);
    }
  } catch (e) {
    console.error({ embedUrls, e });
  }
}
